'use strict';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

window.dataLayer = window.dataLayer || [];

(function () {
    const prefersReducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');

    function setDocumentScrollY(y) {
        const py = Math.max(0, Math.round(y));
        window.scrollTo({ top: py, left: 0, behavior: 'auto' });
        const root = document.scrollingElement;
        if (root && Math.abs(root.scrollTop - py) > 1) {
            root.scrollTop = py;
        }
    }

    const howItState = {
        scene: null,
        pin: null,
        stage: null,
        layers: null,
        panels: [],
        copySlots: [],
        timeline: null,
        trigger: null,
    };

    let cachedNavHeight = 0;

    function refreshNavHeightCache() {
        const navbar = document.querySelector('.navbar');
        cachedNavHeight = navbar ? Math.ceil(navbar.getBoundingClientRect().height) : 0;
    }

    function readPinTopOffsetPx() {
        return cachedNavHeight;
    }

    refreshNavHeightCache();
    const navEl = document.querySelector('.navbar');
    if (navEl && typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(refreshNavHeightCache).observe(navEl);
    } else {
        window.addEventListener('resize', refreshNavHeightCache, { passive: true });
    }

    function landingNeedsScrollLib() {
        const scene = document.querySelector('.anga-how-it-scroll-scene');

        if (scene) {
            return !scene.hasAttribute('data-anga-no-scroll');
        }

        if (document.querySelector('[data-anga-promise-steps]')) {
            return true;
        }

        const steps = document.querySelectorAll('section.step-section');
        if (steps.length < 2) {
            return false;
        }

        // Legacy stack path: skip only when every step opts out.
        let allOptOut = true;
        steps.forEach(function (s) {
            if (!s.hasAttribute('data-anga-no-scroll')) {
                allOptOut = false;
            }
        });
        return !allOptOut;
    }

    /**
     * Legacy wrapper for non-how-it sections only.
     */
    function initStepStacks() {
        const root = document.querySelector('main') || document.body;
        const all = Array.from(root.querySelectorAll('section.step-section'));
        if (all.length < 2) {
            return;
        }

        const groups = [];
        let current = [];

        for (let i = 0; i < all.length; i++) {
            const el = all[i];
            if (current.length === 0) {
                current.push(el);
                continue;
            }
            const prev = current[current.length - 1];
            if (prev.nextElementSibling === el) {
                current.push(el);
            } else {
                groups.push(current);
                current = [el];
            }
        }
        if (current.length) {
            groups.push(current);
        }

        groups.forEach(function (group) {
            if (group.length < 2) {
                return;
            }
            const first = group[0];
            if (first.closest('.anga-how-it-scroll-scene')) {
                return;
            }
            const parent = first.parentNode;
            if (!parent) {
                return;
            }

            const wrap = document.createElement('div');
            wrap.className = 'anga-step-stack';
            if (prefersReducedMq.matches) {
                wrap.classList.add('anga-step-stack--static');
            }
            parent.insertBefore(wrap, first);

            group.forEach(function (section) {
                wrap.appendChild(section);
            });
        });
    }

    function safeRun(fn) {
        try {
            fn();
        } catch (err) {
            // ponytail: one init path must not break the whole landing page
            try {
                window.__angaLastInitError = String(err && err.message ? err.message : err);
            } catch (e2) {
                /* ignore */
            }
        }
    }

    function moveNodeInto(node, parent) {
        if (!node || !parent) {
            return;
        }
        if (node.parentNode !== parent) {
            parent.appendChild(node);
        }
    }

    function ensureHowItPinWrapper(scene, stage) {
        if (!scene || !stage) {
            return stage;
        }

        let pin = scene.querySelector('.anga-how-it-scroll-pin');
        if (pin) {
            return pin;
        }

        pin = document.createElement('div');
        pin.className = 'anga-how-it-scroll-pin';
        pin.setAttribute('data-anga-pin-dynamic', '1');
        scene.insertBefore(pin, stage);
        pin.appendChild(stage);

        const header = document.querySelector('.how-it-works-header');
        if (
            header &&
            header.classList.contains('how-it-works-header') &&
            !scene.contains(header) &&
            (header.compareDocumentPosition(scene) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
        ) {
            pin.insertBefore(header, stage);
            header.setAttribute('data-anga-pin-moved-header', '1');
        }

        return pin;
    }

    function teardownHowItPinWrapper(scene) {
        if (!scene) {
            return;
        }
        const pin = scene.querySelector('.anga-how-it-scroll-pin');
        if (!pin) {
            return;
        }

        const header = pin.querySelector('.how-it-works-header[data-anga-pin-moved-header]');
        const stage = pin.querySelector('.anga-how-it-scroll-stage');
        if (!pin.hasAttribute('data-anga-pin-dynamic')) {
            return;
        }
        if (header) {
            scene.parentNode.insertBefore(header, scene);
            header.removeAttribute('data-anga-pin-moved-header');
        }
        if (stage) {
            scene.insertBefore(stage, pin);
        }
        pin.remove();
    }

    function ensureHowItSceneStructure() {
        const sceneExisting = document.querySelector('.anga-how-it-scroll-scene');
        if (sceneExisting) {
            const stageExisting = sceneExisting.querySelector('.anga-how-it-scroll-stage');
            const layersExisting = stageExisting && stageExisting.querySelector('.anga-how-it-scroll-layers');
            if (stageExisting && layersExisting) {
                let panelsExisting = Array.from(layersExisting.querySelectorAll(':scope > section.step-section'));
                if (panelsExisting.length < 3) {
                    panelsExisting = Array.from(layersExisting.querySelectorAll('section.step-section'));
                }
                if (panelsExisting.length >= 3) {
                    return {
                        scene: sceneExisting,
                        pin: sceneExisting.querySelector('.anga-how-it-scroll-pin') || stageExisting,
                        stage: stageExisting,
                        layers: layersExisting,
                        panels: panelsExisting.slice(0, 3),
                    };
                }
            }
            // Scene already in markup — never rebuild (avoids removeChild on moved nodes).
            return null;
        }

        const allSteps = Array.from(document.querySelectorAll('section.step-section'));
        if (allSteps.length < 3) {
            return null;
        }

        const group = allSteps.slice(0, 3);
        const parent = group[0].parentNode;
        if (!parent) {
            return null;
        }

        const scene = document.createElement('div');
        scene.id = 'cards-track';
        scene.className = 'anga-how-it-scroll-scene';
        const stage = document.createElement('div');
        stage.className = 'anga-how-it-scroll-stage';
        const layers = document.createElement('div');
        layers.className = 'anga-how-it-scroll-layers';
        stage.appendChild(layers);
        scene.appendChild(stage);
        parent.insertBefore(scene, group[0]);

        group.forEach(function (section, idx) {
            moveNodeInto(section, layers);
            section.setAttribute('data-card-index', String(idx));
        });

        return {
            scene,
            pin: stage,
            stage,
            layers,
            panels: group,
        };
    }

    function teardownHowItStickyCopy(stage, panels) {
        if (!stage) {
            return;
        }
        const copyStack = stage.querySelector('.anga-how-it-scroll-copy');
        if (!copyStack) {
            return;
        }

        copyStack.querySelectorAll('.anga-how-it-scroll-copy__slot').forEach(function (slot) {
            const col = slot.querySelector('.col-lg-5[data-anga-copy-col]');
            if (!col) {
                return;
            }
            const idx = parseInt(col.getAttribute('data-anga-copy-col'), 10);
            const panel = panels[idx];
            const row = panel && panel.querySelector('.row');
            if (row) {
                row.insertBefore(col, row.firstChild);
            }
            col.removeAttribute('data-anga-copy-col');
            col.style.removeProperty('opacity');
        });

        copyStack.remove();
    }

    function buildHowItStickyCopy(stage, panels) {
        teardownHowItStickyCopy(stage, panels);

        const layers = stage.querySelector('.anga-how-it-scroll-layers');
        if (!layers) {
            return [];
        }

        const copyStack = document.createElement('div');
        copyStack.className = 'anga-how-it-scroll-copy';
        stage.insertBefore(copyStack, layers);

        const slots = [];

        panels.forEach(function (panel, idx) {
            const col = panel.querySelector('.col-lg-5');
            if (!col) {
                return;
            }

            col.setAttribute('data-anga-copy-col', String(idx));

            const slot = document.createElement('div');
            slot.className = 'anga-how-it-scroll-copy__slot';
            slot.setAttribute('data-step-index', String(idx));

            const container = document.createElement('div');
            container.className = 'container h-100';
            const row = document.createElement('div');
            row.className = 'row align-items-center h-100';
            row.appendChild(col);
            container.appendChild(row);
            slot.appendChild(container);

            copyStack.appendChild(slot);
            slots.push(slot);
        });

        return slots;
    }

    function destroyHowItAnimation() {
        safeRun(function () {
            if (howItState.trigger) {
                howItState.trigger.kill();
                howItState.trigger = null;
            }
        });
        safeRun(function () {
            if (howItState.timeline) {
                howItState.timeline.kill();
                howItState.timeline = null;
            }
        });
        if (howItState.pin) {
            howItState.pin.classList.remove('anga-how-it-scroll-pin--seq', 'anga-how-it-scroll-pin--seq-mobile');
            howItState.pin.style.removeProperty('--how-it-sticky-top');
        }
        if (howItState.scene) {
            howItState.scene.style.removeProperty('min-height');
        }
        if (howItState.stage) {
            howItState.stage.classList.remove(
                'anga-how-it-scroll-stage--seq',
                'anga-how-it-scroll-stage--seq-mobile',
                'anga-how-it-scroll-pin--seq',
                'anga-how-it-scroll-pin--seq-mobile'
            );
            howItState.stage.style.removeProperty('height');
            howItState.stage.style.removeProperty('min-height');
        }
        howItState.copySlots.forEach(function (slot) {
            slot.style.removeProperty('opacity');
        });
        teardownHowItStickyCopy(howItState.stage, howItState.panels);
        teardownHowItPinWrapper(howItState.scene);
        howItState.copySlots = [];
        howItState.pin = null;
        howItState.panels.forEach(function (panel) {
            panel.style.removeProperty('transform');
            panel.style.removeProperty('will-change');
            panel.style.removeProperty('transform-origin');
            panel.style.removeProperty('opacity');
            panel.style.removeProperty('filter');
            panel.style.removeProperty('z-index');
        });
        resetChatTemplateScales();
    }

    function shouldAnimateHowIt() {
        return !prefersReducedMq.matches;
    }

    function isHowItDesktopViewport() {
        return window.innerWidth >= 992;
    }

    function measureHowItStageHeight(panels) {
        let max = 0;
        panels.forEach(function (panel) {
            max = Math.max(max, Math.ceil(panel.getBoundingClientRect().height));
        });
        return Math.max(420, max);
    }

    const HOW_IT_MOBILE_HEADER_GAP = 8;

    /** Mobile stage fills the viewport under nav + title (no empty grey band below the card). */
    function measureHowItMobileStageHeight(pin) {
        const pinTop = readPinTopOffsetPx();
        const header = pin && pin.querySelector('.how-it-works-header');
        const headerH = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
        // Use layout viewport (innerHeight), not visualViewport — URL-bar flicker must not resize the stage.
        const available = window.innerHeight - pinTop - headerH - HOW_IT_MOBILE_HEADER_GAP;
        return Math.max(420, available);
    }

    /** Desktop: GSAP pin + scrub. */
    function createHowItDesktopScrollTrigger(pin, stage, timeline, scrollSpeedMultiplier) {
        const scrollUnit = () => Math.max(220, Math.round(stage.offsetHeight));
        const totalDuration = timeline.totalDuration();
        const pinTop = () => readPinTopOffsetPx();

        return ScrollTrigger.create({
            trigger: pin,
            pin: pin,
            animation: timeline,
            start: () => `top top+=${pinTop()}`,
            end: () => `+=${Math.round(scrollUnit() * totalDuration * scrollSpeedMultiplier)}`,
            scrub: 0.25,
            pinSpacing: true,
            anticipatePin: 1,
            pinType: 'fixed',
            invalidateOnRefresh: true,
        });
    }

    /**
     * Mobile: CSS sticky + scrubbed timeline — no GSAP pin.
     * pinType fixed/transform both jitter on iOS touch scroll; sticky avoids that class of shake.
     * Trigger the tall scene (not the sticky child) so start/end stay stable while stuck.
     */
    function createHowItMobileScrollTrigger(pin, stage, timeline, scrollSpeedMultiplier) {
        const pinTop = () => readPinTopOffsetPx();
        const totalDuration = timeline.totalDuration();
        const scrollDistance = () =>
            Math.max(480, Math.round(stage.offsetHeight * totalDuration * scrollSpeedMultiplier));

        pin.style.setProperty('--how-it-sticky-top', pinTop() + 'px');

        const scene = howItState.scene;
        if (scene) {
            // Spacer so sticky has room to scrub through all steps.
            scene.style.minHeight = pin.offsetHeight + scrollDistance() + 'px';
        }

        return ScrollTrigger.create({
            trigger: scene || pin,
            animation: timeline,
            start: () => `top top+=${pinTop()}`,
            end: () => `+=${scrollDistance()}`,
            // scrub:true = 1:1 with scroll (no catch-up lag that reads as shake)
            scrub: true,
            // no pin — CSS position:sticky holds the card
            invalidateOnRefresh: false,
            onRefresh: function () {
                pin.style.setProperty('--how-it-sticky-top', readPinTopOffsetPx() + 'px');
            },
            onToggle: function (self) {
                panelsWillChange(self.isActive);
            },
        });
    }

    function panelsWillChange(on) {
        howItState.panels.forEach(function (panel) {
            if (on) {
                panel.style.willChange = 'transform';
            } else {
                panel.style.removeProperty('will-change');
            }
        });
    }

    function setupHowItDesktopStackAnimation(pin, stage, panels) {
        pin.classList.add('anga-how-it-scroll-pin--seq');
        stage.classList.add('anga-how-it-scroll-stage--seq');
        panels.forEach(function (panel) {
            panel.style.willChange = 'transform';
        });

        const copySlots = buildHowItStickyCopy(stage, panels);
        howItState.copySlots = copySlots;

        const HOLD_DURATION = 0.6;
        const TRANSITION_DURATION = 0.38;
        const SCROLL_SPEED_MULTIPLIER = 0.82;
        const COPY_FADE = TRANSITION_DURATION * 0.5;

        gsap.set(panels[0], { yPercent: 0, immediateRender: true });
        gsap.set(panels[1], { yPercent: 100, immediateRender: true });
        gsap.set(panels[2], { yPercent: 100, immediateRender: true });

        copySlots.forEach(function (slot, idx) {
            gsap.set(slot, { opacity: idx === 0 ? 1 : 0, immediateRender: true });
        });

        const timeline = gsap.timeline();
        if (copySlots.length >= 3) {
            timeline
                .to(panels[0], { duration: HOLD_DURATION })
                .addLabel('toStep2')
                .to(
                    panels[1],
                    { yPercent: 0, duration: TRANSITION_DURATION, ease: 'none' },
                    'toStep2'
                )
                .to(copySlots[0], { opacity: 0, duration: COPY_FADE, ease: 'none' }, 'toStep2')
                .to(copySlots[1], { opacity: 1, duration: COPY_FADE, ease: 'none' }, 'toStep2')
                .to(panels[1], { duration: HOLD_DURATION })
                .addLabel('toStep3')
                .to(
                    panels[2],
                    { yPercent: 0, duration: TRANSITION_DURATION, ease: 'none' },
                    'toStep3'
                )
                .to(copySlots[1], { opacity: 0, duration: COPY_FADE, ease: 'none' }, 'toStep3')
                .to(copySlots[2], { opacity: 1, duration: COPY_FADE, ease: 'none' }, 'toStep3')
                .to(panels[2], { duration: HOLD_DURATION });
        } else {
            timeline
                .to(panels[0], { duration: HOLD_DURATION })
                .to(panels[1], {
                    yPercent: 0,
                    duration: TRANSITION_DURATION,
                    ease: 'none',
                })
                .to(panels[1], { duration: HOLD_DURATION })
                .to(panels[2], {
                    yPercent: 0,
                    duration: TRANSITION_DURATION,
                    ease: 'none',
                })
                .to(panels[2], { duration: HOLD_DURATION });
        }

        howItState.timeline = timeline;
        howItState.trigger = createHowItDesktopScrollTrigger(pin, stage, timeline, SCROLL_SPEED_MULTIPLIER);
        scheduleFitChatTemplateScales();
    }

    /** Mobile: sticky card + yPercent cover (transforms only; ease none; no GSAP pin). */
    function setupHowItMobileStackAnimation(pin, stage, panels) {
        const stageHeight = measureHowItMobileStageHeight(pin);
        stage.style.height = stageHeight + 'px';
        stage.style.minHeight = stageHeight + 'px';

        pin.classList.add('anga-how-it-scroll-pin--seq', 'anga-how-it-scroll-pin--seq-mobile');
        stage.classList.add('anga-how-it-scroll-stage--seq', 'anga-how-it-scroll-stage--seq-mobile');

        panels.forEach(function (panel) {
            panel.style.transformOrigin = 'center top';
        });

        const HOLD_DURATION = 0.5;
        const TRANSITION_DURATION = 0.4;
        const SCROLL_SPEED_MULTIPLIER = 1.05;

        // Transforms only — later panels always stack above earlier ones (no scrubbed zIndex thrash).
        gsap.set(panels[0], { yPercent: 0, force3D: true, zIndex: 1, immediateRender: true });
        gsap.set(panels[1], { yPercent: 100, force3D: true, zIndex: 2, immediateRender: true });
        gsap.set(panels[2], { yPercent: 100, force3D: true, zIndex: 3, immediateRender: true });

        const timeline = gsap.timeline({ defaults: { ease: 'none' } });
        timeline
            .to(panels[0], { duration: HOLD_DURATION })
            .addLabel('toStep2')
            .to(panels[1], { yPercent: 0, duration: TRANSITION_DURATION }, 'toStep2')
            .to(panels[1], { duration: HOLD_DURATION })
            .addLabel('toStep3')
            .to(panels[2], { yPercent: 0, duration: TRANSITION_DURATION }, 'toStep3')
            .to(panels[2], { duration: HOLD_DURATION });

        howItState.timeline = timeline;
        howItState.trigger = createHowItMobileScrollTrigger(pin, stage, timeline, SCROLL_SPEED_MULTIPLIER);
        scheduleFitChatTemplateScales();
    }

    function resetChatTemplateScales() {
        document.querySelectorAll('.anga-chat-template-scaler').forEach(function (scaler) {
            scaler.style.removeProperty('height');
            scaler.removeAttribute('data-anga-scaled');
            const template = scaler.querySelector('.anga-chat-template');
            if (!template) {
                return;
            }
            template.style.removeProperty('transform');
            template.style.removeProperty('left');
            template.style.removeProperty('margin-left');
        });
    }

    let fitChatRaf = 0;
    function scheduleFitChatTemplateScales() {
        if (fitChatRaf) {
            cancelAnimationFrame(fitChatRaf);
        }
        fitChatRaf = requestAnimationFrame(function () {
            fitChatRaf = requestAnimationFrame(function () {
                fitChatRaf = 0;
                fitChatTemplateScales();
            });
        });
    }

    function fitChatTemplateScales() {
        const phoneW = 357;
        const phoneH = 736;
        const pending = [];

        document.querySelectorAll('.anga-chat-template-scaler').forEach(function (scaler) {
            const template = scaler.querySelector('.anga-chat-template');
            if (!template) {
                return;
            }

            const section = scaler.closest('.step-section');
            const visual = scaler.closest('.step-visual');
            const isMobileStack = !!(section && section.closest('.anga-how-it-scroll-stage--seq-mobile'));
            const width =
                scaler.clientWidth ||
                (visual && visual.clientWidth) ||
                scaler.getBoundingClientRect().width ||
                Math.min(328, Math.max(window.innerWidth - 48, 280));

            let availableHeight = phoneH;
            if (isMobileStack && visual) {
                // Contain phone inside the flex slot — leave a tiny gutter only.
                const visualH = visual.clientHeight || visual.getBoundingClientRect().height;
                if (visualH > 40) {
                    availableHeight = Math.max(visualH - 8, 160);
                } else if (section) {
                    const content = section.querySelector('.step-content');
                    const contentH = content ? content.getBoundingClientRect().height : 0;
                    availableHeight = Math.max(section.clientHeight - contentH - 32, 160);
                }
            } else if (section) {
                availableHeight = Math.max(section.clientHeight - 56, 240);
            }

            const scale = Math.min(width / phoneW, availableHeight / phoneH, 1);
            pending.push({
                template: template,
                scaler: scaler,
                scale: scale,
                height: Math.ceil(phoneH * scale),
            });
        });

        pending.forEach(function (item) {
            item.template.style.left = '50%';
            item.template.style.marginLeft = '0';
            item.template.style.transformOrigin = 'top center';
            item.template.style.transform = 'translateX(-50%) scale(' + item.scale + ')';
            const isMobileStack = !!item.scaler.closest('.anga-how-it-scroll-stage--seq-mobile');
            // Always set explicit height so aspect-ratio cannot blow past the flex slot.
            item.scaler.style.height = item.height + 'px';
            if (isMobileStack) {
                item.scaler.style.maxHeight = '100%';
            }
            item.scaler.setAttribute('data-anga-scaled', '1');
        });
    }

    function setupHowItAnimation() {
        safeRun(function () {
            const structure = ensureHowItSceneStructure();
            if (!structure) {
                return;
            }

            howItState.scene = structure.scene;
            howItState.pin = structure.pin;
            howItState.stage = structure.stage;
            howItState.layers = structure.layers;
            howItState.panels = structure.panels;

            destroyHowItAnimation();

            if (!shouldAnimateHowIt()) {
                scheduleFitChatTemplateScales();
                return;
            }

            const stage = howItState.stage;
            const panels = howItState.panels;
            if (!stage || panels.length < 3) {
                scheduleFitChatTemplateScales();
                return;
            }

            howItState.pin = ensureHowItPinWrapper(howItState.scene, howItState.stage);
            const pin = howItState.pin || howItState.stage;
            if (!pin) {
                scheduleFitChatTemplateScales();
                return;
            }

            if (isHowItDesktopViewport()) {
                setupHowItDesktopStackAnimation(pin, stage, panels);
            } else {
                // How It mobile stack is core UX (Elle Phase 2) — run unless section opts out.
                let sectionMobile = howItState.scene
                    ? howItState.scene.getAttribute('data-anga-animation-mobile')
                    : null;
                if (!sectionMobile && howItState.scene) {
                    const nested = howItState.scene.querySelector('[data-anga-animation-mobile]');
                    if (nested) {
                        sectionMobile = nested.getAttribute('data-anga-animation-mobile');
                    }
                }
                if (sectionMobile === 'no') {
                    scheduleFitChatTemplateScales();
                    return;
                }
                setupHowItMobileStackAnimation(pin, stage, panels);
            }
        });
    }

    function initChatTemplateLoops() {
        scheduleFitChatTemplateScales();

        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced) {
            document.querySelectorAll('.anga-chat-template').forEach(function (template) {
                template.classList.add('anga-chat-template--static');
            });
            return;
        }

        function parseDelaySec(row) {
            const raw = getComputedStyle(row).animationDelay || '0s';
            return parseFloat(raw) || 0;
        }

        function messageAnimSec(rows) {
            const row = rows[0];
            if (!row) {
                return 0.6;
            }
            return parseFloat(getComputedStyle(row).animationDuration) || 0.6;
        }

        function cycleDurationMs(chat) {
            const rows = chat.querySelectorAll('.chat-row');
            let maxDelay = 0;
            rows.forEach(function (row) {
                maxDelay = Math.max(maxDelay, parseDelaySec(row));
            });
            const animSec = messageAnimSec(rows);
            const hasReaction = !!chat.querySelector('.reaction');
            const messageEndMs = (maxDelay + animSec) * 1000;
            const reactionEndMs = (1.9 + 0.62 + 2.0) * 1000;
            const tailMs = hasReaction ? Math.max(messageEndMs, reactionEndMs) : messageEndMs + 2000;
            return tailMs + 500;
        }

        function replayChat(chat) {
            const datePill = chat.querySelector('.date-pill');
            const dateHtml = datePill ? datePill.outerHTML : '';
            const rowsHtml = Array.from(chat.querySelectorAll('.chat-row'))
                .map(function (row) {
                    return row.outerHTML;
                })
                .join('');
            chat.innerHTML = dateHtml + rowsHtml;
        }

        document.querySelectorAll('.anga-chat-template').forEach(function (template) {
            const chat = template.querySelector('[data-anga-chat-loop]');
            if (!chat) {
                return;
            }

            let timer = null;
            let running = false;

            function clearLoop() {
                if (timer) {
                    clearTimeout(timer);
                    timer = null;
                }
            }

            function scheduleNext() {
                clearLoop();
                timer = setTimeout(function () {
                    if (!running) {
                        return;
                    }
                    replayChat(chat);
                    scheduleNext();
                }, cycleDurationMs(chat));
            }

            function start() {
                if (running) {
                    return;
                }
                running = true;
                scheduleNext();
            }

            function stop() {
                running = false;
                clearLoop();
            }

            if ('IntersectionObserver' in window) {
                const obs = new IntersectionObserver(
                    function (entries) {
                        entries.forEach(function (entry) {
                            if (entry.isIntersecting) {
                                start();
                            } else {
                                stop();
                            }
                        });
                    },
                    { root: null, rootMargin: '80px 0px', threshold: 0.15 }
                );
                obs.observe(template);
            } else {
                start();
            }
        });

        let resizeTimer = null;
        window.addEventListener('resize', function () {
            if (resizeTimer) {
                clearTimeout(resizeTimer);
            }
            resizeTimer = setTimeout(scheduleFitChatTemplateScales, 100);
        });
    }

    function initMeetOmarPlayers() {
        document.querySelectorAll('[data-meet-omar-player]').forEach(function (player) {
            const video = player.querySelector('[data-meet-omar-video]');
            const playBtn = player.querySelector('[data-meet-omar-toggle-play]');
            const muteBtn = player.querySelector('[data-meet-omar-toggle-mute]');

            if (!video || !playBtn || !muteBtn) {
                return;
            }

            const lazySrc = (video.dataset.meetOmarSrc || '').trim();

            function attachSrcIfNeeded() {
                if (!lazySrc || video.getAttribute('src') || video.currentSrc) {
                    return;
                }
                video.src = lazySrc;
                video.load();
            }

            function syncUi() {
                const paused = video.paused;
                const muted = video.muted;

                playBtn.classList.toggle('is-paused', paused);
                playBtn.setAttribute('aria-pressed', paused ? 'false' : 'true');

                muteBtn.classList.toggle('is-muted', muted);
                muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
                muteBtn.setAttribute('aria-label', muted ? 'Mute' : 'Unmute');
            }

            playBtn.addEventListener('click', function () {
                attachSrcIfNeeded();
                if (video.paused) {
                    video.play().catch(function () {});
                } else {
                    video.pause();
                }
                syncUi();
            });

            muteBtn.addEventListener('click', function () {
                attachSrcIfNeeded();
                video.muted = !video.muted;
                syncUi();
            });

            video.addEventListener('play', syncUi);
            video.addEventListener('pause', syncUi);
            video.addEventListener('volumechange', syncUi);
            video.addEventListener('loadedmetadata', function () {
                video.play().catch(function () {});
                syncUi();
            });

            if (lazySrc && 'IntersectionObserver' in window) {
                const obs = new IntersectionObserver(
                    function (entries) {
                        entries.forEach(function (entry) {
                            if (entry.isIntersecting) {
                                attachSrcIfNeeded();
                                obs.disconnect();
                            }
                        });
                    },
                    { root: null, rootMargin: '200px 0px', threshold: 0.01 }
                );
                obs.observe(player);
            } else if (lazySrc) {
                attachSrcIfNeeded();
            }

            syncUi();
        });
    }

    function initSnapCarousels() {
        const rafByTrack = new WeakMap();

        function slidesOf(track) {
            return Array.from(track.children).filter(function (el) {
                return el.nodeType === 1;
            });
        }

        function syncSnap(snap) {
            const track = snap.querySelector('.anga-campaign-snap__track');
            const dots = Array.from(snap.querySelectorAll('.anga-campaign-snap__dot'));
            if (!track || dots.length === 0) {
                return;
            }
            const slides = slidesOf(track);
            if (slides.length === 0) {
                return;
            }
            const root = track.getBoundingClientRect();
            let best = 0;
            let bestVis = -1;
            slides.forEach(function (slide, i) {
                const r = slide.getBoundingClientRect();
                const vis = Math.min(r.right, root.right) - Math.max(r.left, root.left);
                if (vis > bestVis) {
                    bestVis = vis;
                    best = i;
                }
            });
            dots.forEach(function (dot, di) {
                const on = di === best;
                dot.classList.toggle('is-active', on);
                if (on) {
                    dot.setAttribute('aria-current', 'true');
                } else {
                    dot.removeAttribute('aria-current');
                }
            });
        }

        document.addEventListener('scroll', function (event) {
            const track = event.target;
            if (!(track instanceof Element) || !track.classList.contains('anga-campaign-snap__track')) {
                return;
            }
            const snap = track.closest('.anga-campaign-snap--peek');
            if (!snap) {
                return;
            }
            if (rafByTrack.get(track)) {
                return;
            }
            rafByTrack.set(track, requestAnimationFrame(function () {
                rafByTrack.delete(track);
                syncSnap(snap);
            }));
        }, true);

        document.addEventListener('click', function (event) {
            const dot = event.target.closest('.anga-campaign-snap__dot');
            if (!dot) {
                return;
            }
            const snap = dot.closest('.anga-campaign-snap--peek');
            const track = snap && snap.querySelector('.anga-campaign-snap__track');
            if (!track) {
                return;
            }
            const dots = Array.from(snap.querySelectorAll('.anga-campaign-snap__dot'));
            const slides = slidesOf(track);
            const slide = slides[dots.indexOf(dot)];
            if (!slide) {
                return;
            }
            const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
            if (Math.abs(delta) < 1) {
                syncSnap(snap);
                return;
            }
            track.scrollTo({
                left: track.scrollLeft + delta,
                behavior: prefersReducedMq.matches ? 'auto' : 'smooth',
            });
        }, true);

        document.querySelectorAll('.anga-campaign-snap--peek').forEach(syncSnap);
    }

    function initReviewPlayers() {
        const frames = Array.from(document.querySelectorAll('[data-anga-review-player]'));

        function mediaOf(frame) {
            return frame.querySelector('video, audio');
        }

        function pauseOthers(except) {
            frames.forEach(function (frame) {
                const media = mediaOf(frame);
                if (media && media !== except && !media.paused) {
                    media.pause();
                }
            });
        }

        function syncFrame(frame) {
            const media = mediaOf(frame);
            const btn = frame.querySelector('[data-anga-review-play]');
            if (!media || !btn) {
                return;
            }
            const playing = !media.paused && !media.ended;
            const keepControls = playing || (media.currentTime > 0.05 && !media.ended);
            btn.hidden = keepControls;
            media.controls = keepControls;
            frame.classList.toggle('is-playing', keepControls);
        }

        frames.forEach(function (frame) {
            const media = mediaOf(frame);
            const btn = frame.querySelector('[data-anga-review-play]');
            if (!media || !btn) {
                return;
            }

            btn.addEventListener('click', function () {
                pauseOthers(media);
                media.muted = false;
                media.volume = 1;
                if (media.tagName === 'VIDEO') {
                    media.loop = false;
                }
                if (media.ended) {
                    media.currentTime = 0;
                }
                media.controls = true;
                media.play().then(function () {
                    syncFrame(frame);
                }).catch(function () {
                    syncFrame(frame);
                });
            });

            media.addEventListener('play', function () {
                pauseOthers(media);
                syncFrame(frame);
            });
            media.addEventListener('pause', syncFrame.bind(null, frame));
            media.addEventListener('ended', function () {
                media.controls = false;
                syncFrame(frame);
            });
        });

        if ('IntersectionObserver' in window) {
            const obs = new IntersectionObserver(
                function (entries) {
                    entries.forEach(function (entry) {
                        if (entry.isIntersecting) {
                            return;
                        }
                        const media = mediaOf(entry.target);
                        if (media && !media.paused) {
                            media.pause();
                        }
                    });
                },
                { threshold: 0.25 }
            );
            frames.forEach(function (frame) {
                obs.observe(frame);
            });
        }
    }

    function getAnchorTarget(hash) {
        if (!hash || hash.length < 2 || hash.charAt(0) !== '#') {
            return null;
        }

        const id = decodeURIComponent(hash.slice(1));
        if (!id) {
            return null;
        }

        return document.getElementById(id) || null;
    }

    function scrollToAnchorHash(hash, _behavior, updateHistory) {
        const target = getAnchorTarget(hash);
        if (!target) {
            return false;
        }

        const navbar = document.querySelector('.navbar');
        const navH = navbar ? navbar.getBoundingClientRect().height : 0;

        function computeScrollTopForElement(el) {
            return Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY - navH - 6));
        }

        const topRounded = computeScrollTopForElement(target);

        if (updateHistory && window.location.hash !== hash) {
            window.history.pushState(null, '', hash);
        }

        const trig = howItState.trigger;
        let pausedHowIt = false;
        if (trig && trig.enabled) {
            trig.disable(false, false);
            pausedHowIt = true;
        }

        setDocumentScrollY(topRounded);

        requestAnimationFrame(function () {
            refreshNavHeightCache();
            ScrollTrigger.refresh();
            const t2 = getAnchorTarget(hash);
            if (t2) {
                const top2 = computeScrollTopForElement(t2);
                if (Math.abs(top2 - window.scrollY) > 2) {
                    setDocumentScrollY(top2);
                }
            }

            if (pausedHowIt && trig) {
                trig.enable(false, false);
            }
        });

        return true;
    }

    document.body.addEventListener('click', function (e) {
        const link = e.target.closest('a[href*="#"]');
        if (!link || link.closest('.accordion-button')) {
            return;
        }
        if (link.hasAttribute('data-bs-toggle')) {
            return;
        }
        const hrefAttr = link.getAttribute('href');
        if (!hrefAttr || hrefAttr === '#') {
            return;
        }

        let hash = '';
        try {
            const url = new URL(hrefAttr, window.location.href);
            const pathMatch = url.pathname === window.location.pathname;
            const originMatch = url.origin === window.location.origin;
            if (!originMatch || !pathMatch) {
                return;
            }
            hash = url.hash;
        } catch (err) {
            return;
        }

        if (!hash || hash.length < 2) {
            return;
        }

        if (scrollToAnchorHash(hash, 'auto', true)) {
            e.preventDefault();
        }
    });

    let layoutRefreshRaf = 0;
    function scheduleLayoutRefresh() {
        if (layoutRefreshRaf) {
            return;
        }
        layoutRefreshRaf = requestAnimationFrame(function () {
            layoutRefreshRaf = 0;
            safeRun(setupHowItAnimation);
            safeRun(function () {
                ScrollTrigger.refresh();
                scheduleFitChatTemplateScales();
            });
        });
    }

    let refreshOnlyRaf = 0;
    let refreshOnlyTimer = 0;
    function scheduleTriggerRefreshOnly() {
        // Debounce visualViewport URL-bar thrash (GSAP refresh is expensive on mobile).
        if (refreshOnlyTimer) {
            clearTimeout(refreshOnlyTimer);
        }
        refreshOnlyTimer = setTimeout(function () {
            refreshOnlyTimer = 0;
            if (refreshOnlyRaf) {
                return;
            }
            refreshOnlyRaf = requestAnimationFrame(function () {
                refreshOnlyRaf = 0;
                safeRun(function () {
                    refreshNavHeightCache();
                    if (
                        howItState.stage &&
                        howItState.pin &&
                        howItState.stage.classList.contains('anga-how-it-scroll-stage--seq-mobile')
                    ) {
                        howItState.pin.style.setProperty(
                            '--how-it-sticky-top',
                            readPinTopOffsetPx() + 'px'
                        );
                        // Only resize + refresh on real layout shifts (orientation), not URL-bar flicker.
                        const nextH = measureHowItMobileStageHeight(howItState.pin);
                        const curH = howItState.stage.offsetHeight;
                        if (Math.abs(nextH - curH) <= 48) {
                            return;
                        }
                        howItState.stage.style.height = nextH + 'px';
                        howItState.stage.style.minHeight = nextH + 'px';
                        if (howItState.scene && howItState.timeline) {
                            const dist = Math.max(
                                480,
                                Math.round(nextH * howItState.timeline.totalDuration() * 1.05)
                            );
                            howItState.scene.style.minHeight =
                                howItState.pin.offsetHeight + dist + 'px';
                        }
                        ScrollTrigger.refresh();
                        scheduleFitChatTemplateScales();
                        return;
                    }
                    ScrollTrigger.refresh();
                    scheduleFitChatTemplateScales();
                });
            });
        }, 180);
    }

    let scrollRaf = 0;
    let preEnterRefreshArmed = true;

    function maybeRefreshHowItBeforeEnter() {
        const trigger = howItState.trigger;
        if (!trigger) {
            return;
        }

        // Skip on mobile — refresh-while-approaching fights transform pin and feels like shake.
        if (!isHowItDesktopViewport()) {
            return;
        }

        if (trigger.isActive) {
            preEnterRefreshArmed = true;
            return;
        }

        const distanceToStart = Math.round(trigger.start - window.scrollY);
        if (distanceToStart > 700) {
            preEnterRefreshArmed = true;
            return;
        }

        if (!preEnterRefreshArmed) {
            return;
        }

        if (distanceToStart >= -24 && distanceToStart <= 420) {
            preEnterRefreshArmed = false;
            ScrollTrigger.refresh();
        }
    }

    function scheduleScrollFrame() {
        if (scrollRaf) {
            return;
        }
        scrollRaf = requestAnimationFrame(function () {
            scrollRaf = 0;
            maybeRefreshHowItBeforeEnter();
            const navbar = document.querySelector('.navbar');
            if (navbar) {
                const scrolled = window.scrollY > 50;
                navbar.style.boxShadow =
                    scrolled && navbar.getAttribute('data-anga-header-transparent') !== '1'
                        ? '0 2px 10px rgba(0,0,0,0.1)'
                        : 'none';
                if (navbar.getAttribute('data-anga-header') !== null) {
                    navbar.classList.toggle('anga-header--scrolled', scrolled);
                }
            }
        });
    }

    safeRun(initMeetOmarPlayers);
    safeRun(initReviewPlayers);
    safeRun(initSnapCarousels);
    safeRun(initChatTemplateLoops);
    window.addEventListener('scroll', scheduleScrollFrame, { passive: true });
    window.addEventListener('resize', scheduleLayoutRefresh);
    // iOS URL bar toggles visualViewport constantly — full rebuild = shake. Refresh only.
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', scheduleTriggerRefreshOnly);
    }
    window.addEventListener('load', function () {
        scheduleTriggerRefreshOnly();

        // Browser hash jump can run before GSAP pin/layout setup; re-apply after load.
        if (window.location.hash) {
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    scrollToAnchorHash(window.location.hash, 'auto', false);
                });
            });
        }
    });

    window.addEventListener('hashchange', function () {
        scrollToAnchorHash(window.location.hash, 'auto', false);
    });

    if (typeof prefersReducedMq.addEventListener === 'function') {
        prefersReducedMq.addEventListener('change', scheduleLayoutRefresh);
    } else if (typeof prefersReducedMq.addListener === 'function') {
        prefersReducedMq.addListener(scheduleLayoutRefresh);
    }

    if (!prefersReducedMq.matches) {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px',
        };
        const observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = '1';
                    entry.target.style.transform = 'translateY(0)';
                }
            });
        }, observerOptions);
        document.querySelectorAll('.step-content, .meet-omar-header').forEach(function (el) {
            if (el.closest('.anga-how-it-scroll-scene') || el.closest('.anga-step-stack')) {
                el.style.opacity = '1';
                el.style.transform = 'none';
                return;
            }
            el.style.opacity = '0';
            el.style.transform = 'translateY(30px)';
            el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
            observer.observe(el);
        });
    }

    document.body.addEventListener('click', function (e) {
        const el = e.target.closest('[data-anga-track]');
        if (!el) {
            return;
        }
        const eventName = el.getAttribute('data-anga-track') || 'anga_interaction';
        const label = el.getAttribute('data-anga-label') || (el.textContent || '').trim().slice(0, 120);
        window.dataLayer.push({
            event: eventName,
            anga_label: label,
            anga_href: el.getAttribute('href') || '',
        });
    });

    // -------------------------------------------------------------------------
    // Anga global config (read from body[data-anga-config] emitted by header partial)
    // -------------------------------------------------------------------------
    function readAngaConfig() {
        const raw = document.body.getAttribute('data-anga-config');
        if (!raw) {
            return {
                animation_enabled: 'yes',
                animation_default_type: 'fade_up',
                animation_default_intensity: 'standard',
                animation_mobile_enabled: 'yes',
                lottie_enabled: 'yes',
                reduced_motion_force_static: 'no',
                default_design_system: 'clean_modern',
            };
        }
        try {
            return JSON.parse(raw);
        } catch (e) {
            return null;
        }
    }

    function motionAllowed(cfg) {
        if (!cfg) {
            return false;
        }
        if (cfg.reduced_motion_force_static === 'yes') {
            return false;
        }
        if (prefersReducedMq.matches) {
            return false;
        }
        if (cfg.animation_enabled === 'no') {
            return false;
        }
        return true;
    }

    function mobileAnimationAllowed(cfg, sectionEl) {
        if (window.innerWidth < 992) {
            let sectionMobile = sectionEl ? sectionEl.getAttribute('data-anga-animation-mobile') : null;
            if (!sectionMobile && sectionEl && typeof sectionEl.querySelector === 'function') {
                const nested = sectionEl.querySelector('[data-anga-animation-mobile]');
                if (nested) {
                    sectionMobile = nested.getAttribute('data-anga-animation-mobile');
                }
            }
            if (sectionMobile) {
                return sectionMobile === 'yes';
            }
            return cfg.animation_mobile_enabled === 'yes';
        }
        return true;
    }

    // -------------------------------------------------------------------------
    // Animation orchestrator — CSS transitions for fade/scale/slide/stagger.
    // GSAP is reserved for gsap_scene (the step scroll scene). No GSAP here.
    // -------------------------------------------------------------------------
    function initAnimationOrchestrator() {
        const cfg = readAngaConfig();
        if (!cfg) {
            return;
        }

        // ponytail: about-promise-steps excluded — its sticky stack owns scroll motion; fade transform breaks sticky + ScrollTrigger
        const sections = document.querySelectorAll('[data-anga-animation-type], .anga-section--meet-omar, .anga-section--testimonial, .anga-section--cta-strip, .anga-section--uae-cta, .anga-section--contact, .anga-section--about-hero, .anga-section--about-promise, .anga-section--about-values, .anga-section--about-who-we-are, .anga-section--about-omar-teaser, .anga-section--about-meet-omar, .anga-section--contact-hero');

        if (sections.length === 0) {
            return;
        }

        if (!motionAllowed(cfg)) {
            // Reduced motion / disabled: ensure everything visible, no transforms.
            sections.forEach(function (el) {
                el.style.opacity = '1';
                el.style.transform = 'none';
            });
            return;
        }

        const intensityMap = { subtle: 16, standard: 28, strong: 44 };
        const typeTransform = {
            fade_up: 'translateY(__DIST__px)',
            fade_in: 'none',
            scale_in: 'scale(0.96)',
            slide_left: 'translateX(-__DIST__px)',
            slide_right: 'translateX(__DIST__px)',
            stagger_cards: 'translateY(__DIST__px)',
            none: 'none',
        };

        // ponytail: fire early — threshold 0.1 + -50px bottom left tall About sections as white voids
        const observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) {
                    return;
                }
                const el = entry.target;
                const delay = parseInt(el.getAttribute('data-anga-animation-delay') || '0', 10);
                setTimeout(function () {
                    el.style.opacity = '1';
                    el.style.transform = 'none';
                }, delay);
                observer.unobserve(el);
            });
        }, { threshold: 0, rootMargin: '120px 0px 0px 0px' });

        sections.forEach(function (el) {
            // Skip sections inside the GSAP scroll scene (handled by GSAP)
            if (el.closest('.anga-how-it-scroll-scene') || el.closest('.anga-step-stack') || el.hasAttribute('data-anga-promise-steps')) {
                el.style.opacity = '1';
                el.style.transform = 'none';
                return;
            }

            if (!mobileAnimationAllowed(cfg, el)) {
                el.style.opacity = '1';
                el.style.transform = 'none';
                return;
            }

            const type = el.getAttribute('data-anga-animation-type') || cfg.animation_default_type || 'fade_up';
            if (type === 'none' || type === 'gsap_scene' || type === 'lottie') {
                el.style.opacity = '1';
                el.style.transform = 'none';
                return;
            }

            const intensity = el.getAttribute('data-anga-animation-intensity') || cfg.animation_default_intensity || 'standard';
            const dist = intensityMap[intensity] || 28;
            const transformTpl = typeTransform[type] || typeTransform.fade_up;
            const transform = transformTpl.replace('__DIST__', String(dist));

            el.style.opacity = '0';
            el.style.transform = transform;
            el.style.transition = 'opacity ' + (cfg.animation_default_intensity === 'subtle' ? '0.4s' : '0.6s') + ' ease, transform 0.6s ease';
            observer.observe(el);
        });
    }

    // -------------------------------------------------------------------------
    // Lottie lazy-loader — dynamic import only when container enters viewport.
    // Reduced-motion / lottie_enabled=no → fallback image stays.
    // -------------------------------------------------------------------------
    function initLottieLoader() {
        const cfg = readAngaConfig();
        if (!cfg) {
            return;
        }
        if (cfg.lottie_enabled !== 'yes') {
            return;
        }
        if (!motionAllowed(cfg)) {
            return;
        }

        const containers = document.querySelectorAll('[data-anga-lottie]:not([data-anga-lottie-init])');
        if (containers.length === 0) {
            return;
        }

        const lottieObserver = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) {
                    return;
                }
                const container = entry.target;
                lottieObserver.unobserve(container);

                const src = container.getAttribute('data-anga-lottie-src');
                if (!src) {
                    return;
                }
                const loop = container.getAttribute('data-anga-lottie-loop') === '1';
                const autoplay = container.getAttribute('data-anga-lottie-autoplay') === '1';
                const speed = parseFloat(container.getAttribute('data-anga-lottie-speed') || '1');

                import(/* webpackChunkName: "lottie-web" */ 'lottie-web')
                    .then(function (mod) {
                        safeRun(function () {
                            const lottie = mod.default || mod;
                            const fallback = container.querySelector('.anga-lottie__fallback');
                            const anim = lottie.loadAnimation({
                                container: container,
                                renderer: 'svg',
                                loop: loop,
                                autoplay: autoplay,
                                path: src,
                            });
                            if (speed && speed !== 1) {
                                anim.setSpeed(speed);
                            }
                            const applyLottieCrop = function () {
                                const crop = container.getAttribute('data-anga-lottie-crop');
                                if (!crop) {
                                    return;
                                }
                                const svg = container.querySelector('svg');
                                if (!svg) {
                                    return;
                                }
                                let viewBox = container.getAttribute('data-anga-lottie-crop-applied');
                                if (!viewBox) {
                                    if (crop !== 'auto') {
                                        viewBox = crop;
                                    } else {
                                        const paths = svg.querySelectorAll('path, rect, circle, ellipse, polygon, polyline');
                                        let minX = Infinity;
                                        let minY = Infinity;
                                        let maxX = -Infinity;
                                        let maxY = -Infinity;
                                        let count = 0;
                                        paths.forEach(function (path) {
                                            try {
                                                const box = path.getBBox();
                                                if (box.width < 2 && box.height < 2) {
                                                    return;
                                                }
                                                if (box.y < 780 || box.y > 1220) {
                                                    return;
                                                }
                                                minX = Math.min(minX, box.x);
                                                minY = Math.min(minY, box.y);
                                                maxX = Math.max(maxX, box.x + box.width);
                                                maxY = Math.max(maxY, box.y + box.height);
                                                count += 1;
                                            } catch (e) {
                                                // getBBox can fail before render
                                            }
                                        });
                                        if (count === 0) {
                                            viewBox = '460 830 420 360';
                                        } else {
                                            const pad = 44;
                                            viewBox = [
                                                Math.floor(minX - pad),
                                                Math.floor(minY - pad),
                                                Math.ceil(maxX - minX + pad * 2),
                                                Math.ceil(maxY - minY + pad * 2),
                                            ].join(' ');
                                        }
                                    }
                                    container.setAttribute('data-anga-lottie-crop-applied', viewBox);
                                }
                                svg.setAttribute('viewBox', viewBox);
                                svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
                                svg.style.width = '100%';
                                svg.style.height = '100%';
                                svg.style.display = 'block';
                            };
                            anim.addEventListener('DOMLoaded', function () {
                                applyLottieCrop();
                                requestAnimationFrame(function () {
                                    applyLottieCrop();
                                    requestAnimationFrame(applyLottieCrop);
                                });
                                anim.addEventListener('enterFrame', applyLottieCrop);
                                container.setAttribute('data-anga-lottie-init', '1');
                                if (fallback) {
                                    fallback.style.display = 'none';
                                }
                            });
                        });
                    })
                    .catch(function () {
                        // Lottie failed to load — fallback image stays visible. No console error.
                    });
            });
        }, { rootMargin: '200px 0px', threshold: 0.01 });

        containers.forEach(function (c) {
            lottieObserver.observe(c);
        });
    }

    function runScrollSetup() {
        safeRun(setupHowItAnimation);
        safeRun(initStepStacks);
        scheduleFitChatTemplateScales();
    }

    if (landingNeedsScrollLib()) {
        const bootScroll = function () {
            runScrollSetup();

            // Ensure deep links still land correctly after ScrollTrigger creates pin spacing.
            if (window.location.hash) {
                requestAnimationFrame(function () {
                    scrollToAnchorHash(window.location.hash, 'auto', false);
                });
            }
        };
        if ('requestIdleCallback' in window) {
            requestIdleCallback(bootScroll, { timeout: 2500 });
        } else {
            setTimeout(bootScroll, 1);
        }
    }

    // Anga design system: animation orchestrator + Lottie lazy-loader.
    // Run after DOM is ready (deferred script guarantees this).
    function initAboutHeroBgZoom() {
        const cfg = readAngaConfig();
        if (!motionAllowed(cfg)) {
            return;
        }

        document.querySelectorAll('.about-hero-section__card').forEach(function (card) {
            card.classList.add('about-hero-section__card--zoom-bg');

            // ponytail: class may already be in markup; force animation restart on load
            const bg = card.querySelector('.about-hero-section__bg');
            if (bg) {
                bg.style.animation = 'none';
                requestAnimationFrame(function () {
                    bg.style.removeProperty('animation');
                });
            }
        });
    }

    // -------------------------------------------------------------------------
    // About promise steps: sticky scroll-stack (mobile + desktop).
    // Cards slide up as the user scrolls; no GSAP pin (avoids iOS shake).
    // Must be declared before safeRun(initPromiseStepsStack) — let is not hoisted.
    // -------------------------------------------------------------------------
    let promiseStepsTrigger = null;

    function initPromiseStepsStack() {
        if (promiseStepsTrigger) {
            promiseStepsTrigger.kill();
            promiseStepsTrigger = null;
        }

        const section = document.querySelector('[data-anga-promise-steps]');
        if (!section) {
            return;
        }

        // Clear any leftover fade-orchestrator styles that break sticky.
        section.style.opacity = '1';
        section.style.transform = 'none';
        section.classList.remove('is-stacking');
        section.removeAttribute('data-anga-ps-state');

        const cfg = readAngaConfig();
        if (!motionAllowed(cfg)) {
            section.setAttribute('data-anga-ps-state', 'skip-motion');
            return;
        }

        const pin = section.querySelector('.about-promise-steps__pin');
        const stack = section.querySelector('.about-promise-steps__stack');
        const card1 = section.querySelector('[data-anga-ps-card="1"]');
        const card2 = section.querySelector('[data-anga-ps-card="2"]');
        const card3 = section.querySelector('[data-anga-ps-card="3"]');
        if (!pin || !stack || !card1 || !card2 || !card3) {
            section.setAttribute('data-anga-ps-state', 'skip-dom');
            return;
        }

        const isDesktop = window.matchMedia('(min-width: 992px)').matches;
        section.classList.add('is-stacking');

        // Initial offsets — cards start pushed down (transforms only).
        gsap.set(card1, { y: 0, scale: 1, force3D: true });
        gsap.set(card2, { y: isDesktop ? 72 : 75, scale: 0.98, force3D: true });
        gsap.set(card3, { y: isDesktop ? 96 : 100, scale: 0.96, force3D: true });

        const scrollDist = isDesktop
            ? () => Math.max(700, Math.round(window.innerHeight * 1.1))
            : () => Math.max(560, Math.round(window.innerHeight * 1.0));

        section.style.minHeight = pin.offsetHeight + scrollDist() + 'px';
        // Sticky sticks within its parent — stretch .container to the runway or pin never locks.
        if (pin.parentElement) {
            pin.parentElement.style.minHeight = section.style.minHeight;
        }

        const navClearance = isDesktop ? 96 : 72;
        const pinStickyTop = () => Math.max(navClearance, Math.round((window.innerHeight - pin.offsetHeight) / 2));

        const tl = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
                trigger: section,
                start: () => `top top+=${pinStickyTop()}`,
                end: () => `+=${scrollDist()}`,
                scrub: true,
                invalidateOnRefresh: true,
            },
        });

        tl.to(card1, { y: -25, scale: 0.94, duration: 1 }, 0);
        tl.to(card2, { y: 0, scale: 1, duration: 1 }, 0);
        tl.to(card3, { y: 0, scale: 1, duration: 1 }, 0.15);

        promiseStepsTrigger = tl.scrollTrigger;
        section.setAttribute('data-anga-ps-state', 'ok');
        ScrollTrigger.refresh();
    }

    safeRun(initAnimationOrchestrator);
    safeRun(initAboutHeroBgZoom);
    safeRun(initLottieLoader);
    safeRun(initPromiseStepsStack);
    window.addEventListener('load', function () {
        safeRun(initPromiseStepsStack);
    });
    document.addEventListener('shortcode.loaded', function () {
        safeRun(initLottieLoader);
        safeRun(initPromiseStepsStack);
        safeRun(initChatTemplateLoops);
    });
    safeRun(initMegaMenus);

    function initMegaMenus() {
        if (typeof bootstrap === 'undefined') {
            return;
        }

        const desktopMq = window.matchMedia('(min-width: 992px)');
        const hideDelayMs = 240;
        const offcanvas = document.getElementById('navbarNav');

        // ponytail: both brand panels anchor to the same .navbar-nav (position:absolute; top:100%),
        // so they overlap exactly. Each menu used to keep its own 240ms hide timer, so moving from
        // du to virgin left both panels open at once and the active state bounced between them.
        // Registry keeps at most one panel open: opening one instantly hides the others.
        const megaInstances = [];

        document.querySelectorAll('.nav-item.anga-mega-menu').forEach(function (item) {
            const toggle = item.querySelector('[data-bs-toggle="dropdown"]');
            const panel = item.querySelector('.anga-mega-panel');
            if (!toggle || !panel) {
                return;
            }

            toggle.setAttribute('data-bs-popper', 'static');
            const dropdown = bootstrap.Dropdown.getOrCreateInstance(toggle, { autoClose: 'outside' });

            const self = { dropdown: dropdown, item: item, hideTimer: null };
            megaInstances.push(self);

            function openMenu() {
                if (!desktopMq.matches) {
                    return;
                }
                clearTimeout(self.hideTimer);
                self.hideTimer = null;
                // Hide any other open mega menu immediately so panels never overlap.
                megaInstances.forEach(function (other) {
                    if (other === self) {
                        return;
                    }
                    clearTimeout(other.hideTimer);
                    other.hideTimer = null;
                    other.dropdown.hide();
                });
                dropdown.show();
            }

            function scheduleClose() {
                if (!desktopMq.matches) {
                    return;
                }
                clearTimeout(self.hideTimer);
                self.hideTimer = setTimeout(function () {
                    dropdown.hide();
                }, hideDelayMs);
            }

            [item, panel].forEach(function (target) {
                target.addEventListener('mouseenter', openMenu);
                target.addEventListener('mouseleave', scheduleClose);
            });

            item.addEventListener('focusin', openMenu);
            item.addEventListener('focusout', function (event) {
                if (!item.contains(event.relatedTarget)) {
                    scheduleClose();
                }
            });

            toggle.addEventListener('click', function (event) {
                event.preventDefault();
            });
        });

        function hideAllOffcanvasDropdowns() {
            if (!offcanvas) {
                return;
            }
            offcanvas.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(function (toggle) {
                bootstrap.Dropdown.getInstance(toggle)?.hide();
            });
        }

        function syncOffcanvasDropdownPopper() {
            if (!offcanvas) {
                return;
            }
            offcanvas.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(function (toggle) {
                if (desktopMq.matches) {
                    if (!toggle.closest('.anga-mega-menu')) {
                        toggle.removeAttribute('data-bs-popper');
                    }
                    return;
                }
                toggle.setAttribute('data-bs-popper', 'static');
            });
        }

        syncOffcanvasDropdownPopper();
        desktopMq.addEventListener('change', syncOffcanvasDropdownPopper);

        if (offcanvas) {
            offcanvas.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(function (toggle) {
                if (toggle.closest('.anga-mega-menu')) {
                    return;
                }
                toggle.addEventListener('click', function (event) {
                    if (!desktopMq.matches) {
                        event.preventDefault();
                    }
                });
            });
        }

        // Mobile offcanvas: accordion — one dropdown/mega panel open at a time.
        if (offcanvas) {
            offcanvas.addEventListener('show.bs.dropdown', function (event) {
                if (desktopMq.matches) {
                    return;
                }
                const openingToggle = event.target.closest('[data-bs-toggle="dropdown"]');
                if (!openingToggle) {
                    return;
                }
                offcanvas.querySelectorAll('[data-bs-toggle="dropdown"].show').forEach(function (toggle) {
                    if (toggle !== openingToggle) {
                        bootstrap.Dropdown.getInstance(toggle)?.hide();
                    }
                });
            });

            offcanvas.addEventListener('hidden.bs.offcanvas', hideAllOffcanvasDropdowns);
        }
    }
})();
